import React, { useCallback, useMemo, useState } from 'react';
import { useAngularService } from 'helpers/get_angular_service';
import Table from '../../components/table';
import api from 'helpers/api';
import sources from 'helpers/sources';
import Link from '../../components/link';
import notification from 'helpers/notification';
import dateFormatter from 'helpers/date_formatter';
import Button from '../../components/button';
import ContactLink from '../../components/contact_link';
import Icon from '../../components/icon';
import useOnMount from 'helpers/use_on_mount';
import TableHeader from 'react/components/table_header';
import Input from 'react/components/input';
import { useExtendState } from 'helpers/use_extend_state';
import useGym from 'helpers/use_gym';
import Container from 'react/components/container';
import PageHeader from 'react/components/page_header';

const WorkoutMembersList = () => {
  const $stateParams = useAngularService('$stateParams');
  const loadingAnimation = useAngularService('loadingAnimation');
  const EmploymentService = useAngularService('EmploymentService');
  const MessageService = useAngularService('MessageService');

  const [permissions] = useState(EmploymentService.get().permissions);
  const [loading, setLoading] = useState(false);
  const [workout, setWorkout] = useState(null);
  const [members, setMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, extendFilters] = useExtendState<any>({});
  const gym = useGym();

  const getWorkout = () => {
    const url = sources.workout(gym.id, $stateParams.workoutId);

    api.get(url).then((data) => {
      setWorkout(data);
    });
  };

  useOnMount(() => {
    getWorkout();
  });

  const count = 25;

  const getMembers = useCallback(
    (page: number) => {
      setLoading(true);
      setCurrentPage(page);
      const url = `${sources.workout(gym.id, $stateParams.workoutId)}/member_workouts`;

      api.get(url, { page, count, search: filters.search }).then((data) => {
        setLoading(false);
        setMembers(data);
        loadingAnimation.hide();
      });
    },
    [gym.id, $stateParams.workoutId, filters.search, loadingAnimation],
  );

  const reload = useCallback(() => {
    getMembers(currentPage);
  }, [currentPage, getMembers]);

  const remove = useCallback(
    (workout) => {
      if (window.confirm('Are you sure you want to remove this workout?')) {
        const url = `/gyms/${gym.id}/contacts/${workout.contact_id}/workouts/${workout.id}`;
        loadingAnimation.show();
        api.delete(url).then(
          () => {
            reload();
          },
          () => {
            notification.error('Request could not be completed. Please try again');
          },
        );
      }
    },
    [gym.id, loadingAnimation, reload],
  );

  const removeAll = () => {
    const messageParams = {
      title: 'Remove workout for all members',
      confirmButtonText: 'Remove workouts',
      message: [
        `This action will remove the workout ${workout.name} from all currently enrolled members.`,
        'Are you sure?',
      ],
    };
    return MessageService.message(messageParams).then(() => {
      const url = `/gyms/${gym.id}/workouts/workouts/${workout.id}/remove_member_workouts`;
      loadingAnimation.show();
      api.delete(url).then(
        () => {
          reload();
        },
        () => {
          notification.error('Request could not be completed. Please try again');
        },
      );
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        id: 'name',
        Cell: ({ row }) => (
          <ContactLink
            id={row.original.contact_id}
            name={row.original.contact_name}
            type={row.original.contact_type}
            status={row.original.contact_status}
          />
        ),
      },
      {
        Header: 'Enrolled in Workout',
        id: 'enrolled_on',
        Cell: ({ row }) => dateFormatter(row.original.enrolled_on),
      },
      {
        Header: 'Last performed Workout',
        id: 'last_completed_on',
        Cell: ({ row }) =>
          row.original.last_completed_on ? dateFormatter(row.original.last_completed_on) : 'N/A',
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row }) =>
          permissions.delete_contact_workouts ? (
            <div className="text-right">
              <Button variant="icon--v2" onClick={() => remove(row.original)}>
                <Icon name="trash" width={18} height={18} />
              </Button>
            </div>
          ) : null,
      },
    ],
    [permissions.delete_contact_workouts, remove],
  );

  return (
    <section data-autoscroll="false" className="content-section">
      <PageHeader multipleRows>
        <h1 className="page-header__title">Members enrolled in {workout?.name}</h1>
        <div className="page-header__buttons">
          <Link type="button" variant="hollow" route="gym.workouts.list">
            Back
          </Link>
          {permissions.delete_contact_workouts && members.length > 0 && (
            <Button variant="primary--v2" onClick={() => removeAll()}>
              Unenrol All
            </Button>
          )}
        </div>
      </PageHeader>

      <Container>
        <main>
          <TableHeader>
            <Input
              value={filters.search}
              onChange={(search) => extendFilters({ search })}
              placeholder="Search by contact"
            />
          </TableHeader>

          <Table
            columns={columns}
            data={members}
            loading={loading}
            emptyText="No members found"
            pageSize={count}
            loadData={(page) => getMembers(page)}
            variant="v2"
            filters={filters}
          />
        </main>
      </Container>
    </section>
  );
};

angular.module('gym').value('WorkoutMembersList', WorkoutMembersList);
